/**
 * WaypointReviewStep - Review and edit waypoints extracted from GPX
 *
 * Features:
 * - List of extracted waypoints
 * - Edit waypoint details (name, type, coordinates)
 * - Delete waypoints
 * - Add manual waypoints
 * - Map preview with all waypoints
 * - Anomaly prompts (mark as rest stop, ignore, etc.)
 *
 * Epic: MVP 1.20.6-S - Key Locations Guided Editor with GPX Intelligence
 * Story: SCRUM-238 (US-1.20.6-S.5-01: Waypoint Review Component)
 *
 * @component
 */

import React, { useState, useCallback, useMemo, useEffect, memo } from 'react';
import PropTypes from 'prop-types';
import Map, { Marker, Source, Layer, NavigationControl } from 'react-map-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import ConfirmModal from '../../../shared/ConfirmModal';
import Button from '../../../common/Button';

/**
 * Waypoint type options
 */
const WAYPOINT_TYPES = [
  { value: 'summit', label: 'Summit' },
  { value: 'camp', label: 'Camp' },
  { value: 'water', label: 'Water Source' },
  { value: 'viewpoint', label: 'Viewpoint' },
  { value: 'rest', label: 'Rest Stop' },
  { value: 'rapid', label: 'Rapid' },
  { value: 'portage', label: 'Portage' },
  { value: 'put_in', label: 'Put-In' },
  { value: 'take_out', label: 'Take-Out' },
  { value: 'poi', label: 'Point of Interest' },
  { value: 'waypoint', label: 'General Waypoint' }
];

/**
 * WaypointReviewStep
 *
 * Step for reviewing and editing GPX-extracted waypoints.
 */
const WaypointReviewStep = memo(function WaypointReviewStep({ waypoints = [], anomalies = [], onChange }) {
  const [editingWaypoint, setEditingWaypoint] = useState(null);
  const [isAddingManual, setIsAddingManual] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState({ isOpen: false, waypoint: null });

  const mapboxToken = import.meta.env.VITE_MAPBOX_API_KEY;

  // Calculate map bounds to fit all waypoints
  const allPoints = useMemo(() => {
    return waypoints.filter(wp => wp.latitude && wp.longitude).map(wp => ({
      lat: wp.latitude,
      lon: wp.longitude,
      name: wp.name
    }));
  }, [waypoints]);

  // Calculate center and zoom level
  const { center, zoom } = useMemo(() => {
    if (allPoints.length === 0) {
      return { center: { lat: 39.8283, lon: -98.5795 }, zoom: 4 };
    }

    if (allPoints.length === 1) {
      return { center: { lat: allPoints[0].lat, lon: allPoints[0].lon }, zoom: 12 };
    }

    // Calculate bounds
    const lats = allPoints.map(p => p.lat);
    const lons = allPoints.map(p => p.lon);
    const minLat = Math.min(...lats);
    const maxLat = Math.max(...lats);
    const minLon = Math.min(...lons);
    const maxLon = Math.max(...lons);

    const centerLat = (minLat + maxLat) / 2;
    const centerLon = (minLon + maxLon) / 2;

    // Estimate zoom level
    const latSpan = maxLat - minLat;
    const lonSpan = maxLon - minLon;
    const maxSpan = Math.max(latSpan, lonSpan);

    let estimatedZoom = 10;
    if (maxSpan < 0.01) {estimatedZoom = 14;}
    else if (maxSpan < 0.05) {estimatedZoom = 12;}
    else if (maxSpan < 0.1) {estimatedZoom = 11;}
    else if (maxSpan < 0.5) {estimatedZoom = 9;}
    else if (maxSpan < 1) {estimatedZoom = 8;}
    else if (maxSpan < 5) {estimatedZoom = 6;}

    return { center: { lat: centerLat, lon: centerLon }, zoom: estimatedZoom };
  }, [allPoints]);

  // Map viewport state
  const [viewState, setViewState] = useState({
    latitude: center.lat,
    longitude: center.lon,
    zoom: zoom
  });

  // Update viewport when points change
  useEffect(() => {
    setViewState({
      latitude: center.lat,
      longitude: center.lon,
      zoom: zoom
    });
  }, [center.lat, center.lon, zoom]);

  // Create GeoJSON for route line connecting waypoints
  const routeGeoJSON = useMemo(() => {
    if (allPoints.length < 2) {return null;}

    return {
      type: 'Feature',
      geometry: {
        type: 'LineString',
        coordinates: allPoints.map(p => [p.lon, p.lat])
      }
    };
  }, [allPoints]);

  const hasValidMapData = allPoints.length > 0 && mapboxToken;

  // Handle waypoint edit
  const handleEditWaypoint = useCallback((waypoint) => {
    setEditingWaypoint({ ...waypoint });
  }, []);

  // Handle save edit
  const handleSaveEdit = useCallback(() => {
    if (!editingWaypoint) {return;}

    const updatedWaypoints = waypoints.map(wp =>
      (wp.id || wp.name) === (editingWaypoint.id || editingWaypoint.name)
        ? editingWaypoint
        : wp
    );

    onChange(updatedWaypoints);
    setEditingWaypoint(null);
  }, [editingWaypoint, waypoints, onChange]);

  // Handle cancel edit
  const handleCancelEdit = useCallback(() => {
    setEditingWaypoint(null);
  }, []);

  // Handle delete waypoint
  const handleDeleteWaypoint = useCallback((waypoint) => {
    const updatedWaypoints = waypoints.filter(
      wp => (wp.id || wp.name) !== (waypoint.id || waypoint.name)
    );

    onChange(updatedWaypoints);
  }, [waypoints, onChange]);

  // Handle add manual waypoint
  const handleAddManualWaypoint = useCallback(() => {
    const newWaypoint = {
      id: `manual-${Date.now()}`,
      name: 'New Waypoint',
      latitude: null,
      longitude: null,
      type: 'waypoint',
      elevation_ft: null,
      mile_marker: null,
      description: '',
      gpx_derived: false,
      sequence_order: waypoints.length + 1
    };

    setEditingWaypoint(newWaypoint);
    setIsAddingManual(true);
  }, [waypoints.length]);

  // Handle save manual waypoint
  const handleSaveManual = useCallback(() => {
    if (!editingWaypoint) {return;}

    onChange([...waypoints, editingWaypoint]);
    setEditingWaypoint(null);
    setIsAddingManual(false);
  }, [editingWaypoint, waypoints, onChange]);

  return (
    <div className="waypoint-review-step space-y-6">
      {/* Header */}
      <div>
        <h3 className="text-lg font-semibold text-gray-900">
          Review Waypoints
        </h3>
        <p className="mt-1 text-sm text-gray-600">
          {waypoints.length} waypoint{waypoints.length !== 1 ? 's' : ''} extracted from GPX.
          Review, edit, or add new waypoints.
        </p>
      </div>

      {/* Anomalies Warning */}
      {anomalies && anomalies.length > 0 && (
        <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
          <div className="flex items-start gap-3">
            <svg
              className="w-5 h-5 text-amber-600 mt-0.5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
              />
            </svg>
            <div className="flex-1">
              <p className="text-sm font-medium text-amber-900">
                {anomalies.length} potential issue{anomalies.length !== 1 ? 's' : ''} detected
              </p>
              <ul className="mt-2 text-xs text-amber-700 space-y-1">
                {anomalies.slice(0, 3).map((anomaly, idx) => (
                  <li key={idx}>• {anomaly.message}</li>
                ))}
              </ul>
              {anomalies.length > 3 && (
                <p className="mt-2 text-xs text-amber-600 italic">
                  +{anomalies.length - 3} more
                </p>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Waypoint List */}
      <div className="space-y-3">
        {waypoints.map((waypoint, index) => (
          <div
            key={waypoint.id || waypoint.name || index}
            className="p-4 bg-white border border-gray-200 rounded-lg hover:border-gray-300 transition-colors"
          >
            <div className="flex items-start justify-between gap-4">
              {/* Waypoint Info */}
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-2">
                  <h4 className="font-medium text-gray-900">{waypoint.name}</h4>
                  <span className="px-2 py-0.5 text-xs font-medium bg-blue-100 text-blue-800 rounded">
                    {WAYPOINT_TYPES.find(t => t.value === waypoint.type)?.label || waypoint.type}
                  </span>
                  {waypoint.gpx_derived && (
                    <span className="px-2 py-0.5 text-xs font-medium bg-gray-100 text-gray-600 rounded">
                      GPX
                    </span>
                  )}
                </div>

                <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs text-gray-600">
                  {waypoint.latitude && waypoint.longitude && (
                    <div>
                      <span className="font-medium">Coordinates:</span> {waypoint.latitude.toFixed(4)}, {waypoint.longitude.toFixed(4)}
                    </div>
                  )}
                  {waypoint.elevation_ft && (
                    <div>
                      <span className="font-medium">Elevation:</span> {waypoint.elevation_ft} ft
                    </div>
                  )}
                  {waypoint.mile_marker !== null && waypoint.mile_marker !== undefined && (
                    <div>
                      <span className="font-medium">Mile Marker:</span> {waypoint.mile_marker} mi
                    </div>
                  )}
                </div>

                {waypoint.description && (
                  <p className="mt-2 text-sm text-gray-600">{waypoint.description}</p>
                )}
              </div>

              {/* Actions */}
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => handleEditWaypoint(waypoint)}
                  className="p-2 text-blue-600 hover:bg-blue-50 rounded transition-colors"
                  title="Edit waypoint"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                    />
                  </svg>
                </button>

                <button
                  type="button"
                  onClick={() => setDeleteConfirm({ isOpen: true, waypoint })}
                  className="p-2 text-red-600 hover:bg-red-50 rounded transition-colors"
                  title="Delete waypoint"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                    />
                  </svg>
                </button>
              </div>
            </div>
          </div>
        ))}

        {/* Empty State */}
        {waypoints.length === 0 && (
          <div className="text-center py-12 text-gray-500">
            <svg
              className="w-12 h-12 mx-auto mb-3 text-gray-400"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
              />
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
              />
            </svg>
            <p className="text-sm">No waypoints yet</p>
            <p className="text-xs text-gray-400 mt-1">Add manual waypoints below</p>
          </div>
        )}
      </div>

      {/* Add Manual Waypoint Button */}
      <button
        type="button"
        onClick={handleAddManualWaypoint}
        className="w-full py-3 text-sm font-medium text-blue-600 bg-blue-50 border-2 border-dashed border-blue-300 rounded-lg hover:bg-blue-100 transition-colors"
      >
        + Add Manual Waypoint
      </button>

      {/* Edit/Add Waypoint Modal */}
      {editingWaypoint && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
            <h4 className="text-lg font-semibold text-gray-900 mb-4">
              {isAddingManual ? 'Add Waypoint' : 'Edit Waypoint'}
            </h4>

            <div className="space-y-4">
              {/* Name */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Name
                </label>
                <input
                  type="text"
                  value={editingWaypoint.name}
                  onChange={(e) =>
                    setEditingWaypoint({ ...editingWaypoint, name: e.target.value })
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              {/* Type */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Type
                </label>
                <select
                  value={editingWaypoint.type}
                  onChange={(e) =>
                    setEditingWaypoint({ ...editingWaypoint, type: e.target.value })
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  {WAYPOINT_TYPES.map(type => (
                    <option key={type.value} value={type.value}>
                      {type.label}
                    </option>
                  ))}
                </select>
              </div>

              {/* Coordinates */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Latitude
                  </label>
                  <input
                    type="number"
                    step="any"
                    value={editingWaypoint.latitude || ''}
                    onChange={(e) =>
                      setEditingWaypoint({
                        ...editingWaypoint,
                        latitude: parseFloat(e.target.value) || null
                      })
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Longitude
                  </label>
                  <input
                    type="number"
                    step="any"
                    value={editingWaypoint.longitude || ''}
                    onChange={(e) =>
                      setEditingWaypoint({
                        ...editingWaypoint,
                        longitude: parseFloat(e.target.value) || null
                      })
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>

              {/* Description */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Description <span className="text-gray-400">(optional)</span>
                </label>
                <textarea
                  value={editingWaypoint.description || ''}
                  onChange={(e) =>
                    setEditingWaypoint({ ...editingWaypoint, description: e.target.value })
                  }
                  rows={2}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>

            {/* Modal Actions */}
            <div className="flex gap-3 mt-6">
              <Button
                type="button"
                onClick={isAddingManual ? handleSaveManual : handleSaveEdit}
                className="flex-1"
              >
                Save
              </Button>
              <button
                type="button"
                onClick={() => {
                  handleCancelEdit();
                  setIsAddingManual(false);
                }}
                className="flex-1 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Interactive Map with All Waypoints */}
      {hasValidMapData && (
        <div className="space-y-2">
          <label className="block text-sm font-medium text-gray-700">
            Waypoints Map Preview
            <span className="ml-2 text-xs font-normal text-gray-500">
              (Click a waypoint marker to view details)
            </span>
          </label>
          <div className="rounded-lg overflow-hidden border border-gray-300 shadow-sm" style={{ height: '500px' }}>
            <Map
              {...viewState}
              onMove={(evt) => setViewState(evt.viewState)}
              mapboxAccessToken={mapboxToken}
              mapStyle="mapbox://styles/mapbox/outdoors-v12"
              style={{ width: '100%', height: '100%' }}
            >
              <NavigationControl position="top-right" />

              {/* Route Line connecting waypoints */}
              {routeGeoJSON && (
                <Source id="route" type="geojson" data={routeGeoJSON}>
                  <Layer
                    id="route-line"
                    type="line"
                    paint={{
                      'line-color': '#9ca3af',
                      'line-width': 2,
                      'line-opacity': 0.6,
                      'line-dasharray': [2, 2]
                    }}
                  />
                </Source>
              )}

              {/* Waypoint Markers */}
              {waypoints.map((waypoint, index) => {
                if (!waypoint.latitude || !waypoint.longitude) {return null;}

                return (
                  <Marker
                    key={waypoint.id || index}
                    latitude={waypoint.latitude}
                    longitude={waypoint.longitude}
                  >
                    <div className="relative">
                      <div className="w-8 h-8 bg-blue-600 text-white rounded-full flex items-center justify-center text-sm font-bold shadow-lg border-2 border-white hover:bg-blue-700 transition-colors cursor-pointer">
                        {index + 1}
                      </div>
                      <div className="absolute -top-9 left-1/2 -translate-x-1/2 px-2 py-1 bg-white border border-gray-300 rounded shadow-md whitespace-nowrap text-xs font-medium pointer-events-none">
                        {waypoint.name}
                      </div>
                    </div>
                  </Marker>
                );
              })}
            </Map>
          </div>
          <p className="text-xs text-gray-500 italic">
            {waypoints.length} waypoint{waypoints.length !== 1 ? 's' : ''} shown on map
            {routeGeoJSON && ' • Connected with route line'}
          </p>
        </div>
      )}

      {!mapboxToken && allPoints.length > 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
          <p className="text-sm text-amber-800">
            Map preview unavailable: Mapbox API key not configured
          </p>
        </div>
      )}

      {allPoints.length === 0 && (
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
          <p className="text-sm text-gray-600">
            No waypoints with coordinates yet - add coordinates to see them on the map
          </p>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      <ConfirmModal
        isOpen={deleteConfirm.isOpen}
        onClose={() => setDeleteConfirm({ isOpen: false, waypoint: null })}
        onConfirm={() => {
          if (deleteConfirm.waypoint) {
            handleDeleteWaypoint(deleteConfirm.waypoint);
          }
          setDeleteConfirm({ isOpen: false, waypoint: null });
        }}
        title="Delete Waypoint"
        message={`Delete waypoint "${deleteConfirm.waypoint?.name}"?`}
        confirmText="Delete"
        cancelText="Cancel"
        confirmButtonClass="bg-red-600 hover:bg-red-700"
      />
    </div>
  );
});

WaypointReviewStep.propTypes = {
  /**
   * Waypoints to review/edit
   */
  waypoints: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      name: PropTypes.string,
      latitude: PropTypes.number,
      longitude: PropTypes.number,
      type: PropTypes.string,
      elevation_ft: PropTypes.number,
      mile_marker: PropTypes.number,
      description: PropTypes.string,
      gpx_derived: PropTypes.bool,
      sequence_order: PropTypes.number
    })
  ),

  /**
   * Anomalies detected in GPX analysis
   */
  anomalies: PropTypes.arrayOf(
    PropTypes.shape({
      type: PropTypes.string,
      message: PropTypes.string,
      severity: PropTypes.string
    })
  ),

  /**
   * Change handler
   */
  onChange: PropTypes.func.isRequired
};

export default WaypointReviewStep;
